import { useMediaQuery } from '@hover/blueprint';
import { useCallback } from 'react';

import { logError } from 'src/shared/telemetry/logger';
import { hasNativeShare } from 'src/shared/utils/environment';

// Desktop browsers have started to support native share, but for most use cases
// on desktop we want to use our own custom share logic.
export const useHasValidNativeShare = () => {
  const [isTouchDevice] = useMediaQuery('(hover: none)');

  return hasNativeShare() && isTouchDevice;
};

interface UseImageNativeShareParams {
  onShareFail?: () => void;
}
interface ImageNativeShareParams {
  url: string;
  title?: string;
  text?: string;
  onShareFail?: () => void | undefined;
  onNonNativeShare?: () => void;
}

/**
 * The text field carries over to copy actions on mobile devices (eg. the user opts to copy the shared item instead of
 * sending in an email or text). This should be used cautiously as the text may interfere with the users ability to
 * use the copied link, as the text will be copied along with the link.
 */
export const useNativeShareHandler = ({
  onShareFail,
}: UseImageNativeShareParams) => {
  const shouldUseNativeShare = useHasValidNativeShare();

  return useCallback(
    async ({ url, title, text, onNonNativeShare }: ImageNativeShareParams) => {
      const shareData = { title, text, url };

      try {
        if (shouldUseNativeShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
        } else if (onNonNativeShare) {
          onNonNativeShare();
        }
      } catch (err) {
        const isAbortError =
          err instanceof DOMException && err.name === 'AbortError';
        if (!isAbortError) {
          logError(err as Error);
        }
        if (onShareFail) {
          onShareFail();
        }
      }
    },
    [onShareFail, shouldUseNativeShare],
  );
};
